import React, { useState, useEffect } from 'react';
import {
    Container,
    Grid,
    Typography,
    Box,
    CircularProgress,
    Tabs,
    Tab,
    Paper,
} from '@mui/material';
import { VideoCall } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { meetingService } from '@services/api';
import MeetingCard from '../components/Meeting/MeetingCard';
import { toast } from 'react-toastify';
import TaleButton from "@components/TaleButton";
export const MEETING_STATUS = {
    SCHEDULED: 'SCHEDULED',
    STARTED: 'STARTED',
    ENDED: 'ENDED',
    CANCELLED: 'CANCELLED'
};


const MeetingList = () => {
    const navigate = useNavigate();
    const [meetings, setMeetings] = useState([]);
    const [filteredMeetings, setFilteredMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);

    const tabs = [
        { label: 'All', filter: null },
        { label: 'Scheduled', filter: MEETING_STATUS.SCHEDULED },
        { label: 'Completed', filter: MEETING_STATUS.ENDED },
        { label: 'Cancelled', filter: MEETING_STATUS.CANCELLED },
    ];

    useEffect(() => {
        loadMeetings();
    }, []);

    useEffect(() => {
        filterMeetings();
    }, [activeTab, meetings]);

    const loadMeetings = async () => {
        setLoading(true);
        try {
            const response = await meetingService.getMeetings();
            setMeetings(response.data);
        } catch (error) {
            toast.error('Failed to load meetings');
        } finally {
            setLoading(false);
        }
    };

    const filterMeetings = () => {
        const currentFilter = tabs[activeTab].filter;
        if (!currentFilter) {
            setFilteredMeetings(meetings);
        } else {
            setFilteredMeetings(meetings.filter(m => m.status === currentFilter));
        }
    };

    const handleDeleteMeeting = async (id) => {
        try {
            await meetingService.deleteMeeting(id);
            toast.success('Meeting deleted successfully');
            loadMeetings();
        } catch (error) {
            toast.error('Failed to delete meeting');
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Container maxWidth="fullWidth" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className="bg-white rounded-2xl backdrop-blur-sm border-b shadow-sm top-0 z-50 print:border-none p-6">
                <Typography variant="h4" fontWeight="bold">
                    My Interviews
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TaleButton
                        role="normal"
                        variant="outlined"
                        onClick={loadMeetings}
                        disabled={loading}
                    >
                        <i className="fas fa-sync-alt mr-2"></i>Refresh
                    </TaleButton>
                    <TaleButton
                        role={"normal"}
                        variant="filled"
                        onClick={() => navigate('/interviews/create')}
                    >
                        <VideoCall className="mr-2"/> Create Meeting
                    </TaleButton>
                </Box>
            </Box>

            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    {tabs.map((tab, index) => (
                        <Tab key={index} label={tab.label} />
                    ))}
                </Tabs>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : filteredMeetings.length > 0 ? (
                <Grid container spacing={3}>
                    {filteredMeetings.map((meeting) => (
                        <Grid item xs={12} md={6} lg={4} key={meeting.id}>
                            <MeetingCard meeting={meeting} onDelete={handleDeleteMeeting} />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Paper sx={{ p: 6, textAlign: 'center', alignItems: 'center', display: 'flex', flexDirection: 'column', borderRadius: '1rem' }}>
                    <Typography variant="h6" gutterBottom>
                        No meetings found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        {activeTab === 0
                            ? 'Create your first meeting to get started'
                            : `No ${tabs[activeTab].label.toLowerCase()} meetings`}
                    </Typography>
                    {activeTab === 0 && (
                        <TaleButton
                            className="w-1/5"
                            role={"normal"}
                            variant="filled"
                            onClick={() => navigate('/interviews/create')}
                        >
                            <VideoCall className="mr-2"/> Create Meeting
                        </TaleButton>
                    )}
                </Paper>
            )}
        </Container>
    );
};

export default MeetingList;